/**
 * The extracted door into "Add a criterion" — Story 20.3.
 *
 * DECISION-077 named the gap in its own title: "an extracted criterion has no
 * door yet". Story 7.1's door creates an `answer_ref` criterion from a question;
 * this one creates an `extracted` criterion from a field the extraction card
 * defines.
 *
 * AT THE INSPECTOR'S OWN WEIGHT. The Qualify tab carried a second 25 of its own
 * until this story, and a second copy is the one that stops agreeing — so there is
 * one, and since Story 27.2 it is the vocabulary's (`DEFAULT_CRITERION_WEIGHT`),
 * which every door and every proposal starts a criterion at.
 *
 * IN ITS FIELD'S SHAPE, SCORING NOTHING (DECISION-143). The map is the kind the
 * field's type gets — each value of a One of, both sides of a Yes/no, a range for
 * a Number, present and absent for Text and Date — with every number at zero,
 * because the operator enters every number: `emptyPointsFor` picks the shape, and
 * the points editor Story 20.8 built is where the numbers go. Until that story the
 * door wrote an empty `options` map whatever the type (Q-103), and the editor
 * still reads one, as one range or two rows scoring 0.
 */
import { applyOperation, type ExtractionField, type Funnel } from '@leadgible/schema'

/** The fields no extracted criterion reads yet — what the second group offers. */
export function unscoredFields(funnel: Funnel): ExtractionField[] {
  const scored = new Set(
    funnel.rubric.criteria.flatMap((criterion) =>
      criterion.source === 'extracted' ? [criterion.field_key] : [],
    ),
  )
  return funnel.rubric.extraction.filter((field) => !scored.has(field.field_key))
}

/**
 * Creates the extracted criterion for a field, as a recipe for `edit()`.
 *
 * A field that does not exist, or one a criterion already scores, changes
 * nothing. The select never offers either, and a recipe should not depend on
 * that.
 *
 * THE VOCABULARY'S `set_criterion` since Story 27.2, which writes what this
 * wrote: the field's name, the default weight, the field's shape at zero, and
 * `crit_{key}` — or the first free `crit_{key}_N`, because the curated
 * blueprints name answer criteria `crit_budget`, and a field called `budget`
 * would otherwise write a duplicate the rubric refuses.
 */
export function addExtractedCriterion(draft: Funnel, fieldKey: string): void {
  if (!unscoredFields(draft).some((candidate) => candidate.field_key === fieldKey)) return
  applyOperation(draft, { op: 'set_criterion', source: 'extracted', field_key: fieldKey })
}
